import React, { useState, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch } from 'react-redux';
import { useDarkMode } from 'Hooks/useDarkMode';

import { DATE_FORMAT_WEB } from 'Constants/DateFormatConstants';
import { setLoader } from 'Store/Actions/Loading/LoadingAction';
import { TicketApiService } from 'Services/ApiServices';
import { DateTimeFormater } from 'Helpers/DateTimeHelper';
import F2WCustomLogger from 'Services/F2WCustomLogger';

const createServerSideDatasource = (dispatch, gridRef) => {
  return {
    getRows: async (params) => {
      try {
        const response = await TicketApiService.getAgGridData('staff/agGridTable/New', params.request);
        const data = response?.data || response;
        if (data?.rows) {
          if (data.totalRows === 0) {
            gridRef.current.api.showNoRowsOverlay();
          }
          params.success({ rowData: data.rows, rowCount: data.rowCount });
        } else {
          params.fail();
        }
      } catch (error) {
        F2WCustomLogger.error('Error fetching grid data', error, {
          view: 'New',
          startRow: params.request.startRow,
          endRow: params.request.endRow,
        });
        params.fail();
      } finally {
        dispatch(setLoader(false));
      }
    },
  };
};

const NewAgGridTable = () => {
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const { agGridTheme } = useDarkMode();

  const [columnDefs] = useState([
    {
      headerName: 'First Name',
      field: 'firstName',
      filter: 'agTextColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      sortable: true,
      width: 150,
    },
    {
      headerName: 'Last Name',
      field: 'lastName',
      filter: 'agTextColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      sortable: true,
      width: 150,
    },
    {
      headerName: 'Destination',
      field: 'destination',
      filter: 'agTextColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      sortable: true,
      width: 150,
    },
    {
      field: 'country',
      headerName: 'Country',
      filter: 'agTextColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      sortable: true,
      width: 150,
    },
    {
      headerName: 'IATA Code',
      field: 'iataCode',
      filter: 'agTextColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      sortable: true,
      width: 110,
    },
    {
      headerName: 'Season',
      field: 'season',
      filter: 'agTextColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      sortable: true,
      width: 150,
    },
    {
      headerName: 'Source Market (staff)',
      field: 'staffSourceMarket',
      filter: 'agTextColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      sortable: true,
      width: 150,
    },
    {
      headerName: 'Planned assigment date',
      field: 'plannedAssignmentStartDate',
      filter: 'agDateColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      sortable: true,
      width: 150,
      valueFormatter: (params) => {
        if (params.value) {
          return DateTimeFormater(params.value, null, DATE_FORMAT_WEB);
        }
      },
    },
    {
      headerName: 'Type of flight',
      field: 'typeOfFlight',
      filter: 'agTextColumnFilter',
      filterParams: { apply: true, newRowsAction: 'keep' },
      suppressFloatingFilterButton: true,
      width: 150,
    },
  ]);

  const [defaultColDef] = useState({
    flex: 1,
    minWidth: 100,
    resizable: true,
    floatingFilter: true,
  });

  const onGridReady = (params) => {
    params.api.updateGridOptions({ serverSideDatasource: createServerSideDatasource(dispatch, gridRef) });
  };

  const onRowDoubleClicked = (event) => {
    const url = `${location.protocol}//${location.host}/new/${event.data.id}`;
    window.open(url, '_blank');
  };

  return (
    <React.Fragment>
      <div className="w-100 h-100">
        <div className={`${agGridTheme} ag-grid-responsive`}>
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="serverSide"
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={100}
            cacheBlockSize={100}
            maxBlocksInCache={1}
            animateRows={true}
            columnHoverHighlight={true}
            debug={false}
            onRowDoubleClicked={onRowDoubleClicked}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default NewAgGridTable;
